import { AccountType, RecordType } from '@xbcb/shared-types';
import {
  CreateWorkOrderTaskDocument,
  WorkOrderMilestone,
  WorkOrderMilestoneName,
} from '__generated__/graphql';
import { WorkOrderTaskType } from '@xbcb/work-order-types';
import { isEuCustomsDeclaration } from '@xbcb/work-order-utils/dist/isEuCustomsDeclaration';
import { getBusinessHourDeadline } from '@xbcb/work-order-utils/dist/getBusinessHourDeadline';
import { getRecordType } from '@xbcb/core';
import { message } from 'antd';
import { safeGetMessage } from '@xbcb/ui-utils';
import { AppRecordKebabMenuItem } from '../../types';
import { executeMutation } from 'libs/executeMutation';
import { fetchLatestRecordAndExecuteMutation } from 'libs/fetchLatestRecordAndExecuteMutation';
import { updateWorkOrderStatus } from 'libs/sharedQueries';
import { isCbamDetectionEnabled } from 'featureFlags';

const TASK_TYPE =
  WorkOrderTaskType.CUSTOMS_DECLARATION_UPLOAD_MISSING_CBAM_DECLARANT_ID;

export const appRecordCreateCbamMissingIdTaskMenuItem: AppRecordKebabMenuItem =
  {
    text: ({ appRecordBundle }) =>
      safeGetMessage(
        appRecordBundle,
        'create_cbam_missing_id_task',
        undefined,
        'Create CBAM missing ID task',
      ),
    key: 'createCbamMissingIdTask',
    action: async ({ record }) => {
      const { id: workOrderId, stateMachine, tasks, operator } = record;
      const recordType = getRecordType(workOrderId) as RecordType;

      // Block task creation if declarant ID already exists on the entry
      const cbam = (record as any).cbam;
      if (cbam?.declarantId) {
        const representationType = cbam.representationType;
        let liableParty: string;
        if (representationType === 'INDIRECT') {
          liableParty = 'Broker';
        } else if (cbam.declarantImporterEntity?.id) {
          liableParty = 'Importer';
        } else if (cbam.declarant?.id?.startsWith('shipper')) {
          liableParty = 'Shipper';
        } else {
          liableParty = 'IOR';
        }
        void message.error(
          `Cannot create task because ${liableParty} already has a CBAM Declarant ID.`,
        );
        return;
      }

      // Check for existing open CBAM task
      const existingTask = tasks?.find(
        (task: any) =>
          task.definition?.workOrderTaskType === TASK_TYPE &&
          ['NOT_ASSIGNED', 'ASSIGNED', 'IN_PROGRESS', 'BLOCKED'].includes(
            task.status,
          ),
      );
      if (existingTask) {
        void message.error('A CBAM missing ID task is already open.');
        return;
      }

      // Find task definition in state machine
      const taskDefinition = stateMachine?.states?.find(
        (state: any) => state.taskDefinition?.workOrderTaskType === TASK_TYPE,
      )?.taskDefinition;
      if (!taskDefinition) {
        void message.error('CBAM task definition not found in state machine.');
        return;
      }

      try {
        await executeMutation({
          mutation: CreateWorkOrderTaskDocument,
          variables: {
            idempotencyKey: `${workOrderId}_${TASK_TYPE}_${tasks?.length}`,
            input: {
              workOrder: { id: workOrderId },
              definition: {
                id: taskDefinition.id,
                version: taskDefinition.version,
              },
              operator: { id: operator?.id },
              deadline: getBusinessHourDeadline(360),
            },
          },
        });
        void message.success('CBAM missing ID task created successfully.');

        // Reopen work order if completed
        if (record.status === 'COMPLETED') {
          await fetchLatestRecordAndExecuteMutation({
            id: workOrderId,
            fields: 'status tasks { id definition { workOrderTaskType }}',
            recordType,
            mutation: updateWorkOrderStatus,
            successMessage: 'Work Order is now reopened.',
            constructVariablesWithIdVersion: (id, version) => ({
              id,
              version,
              status: 'IN_PROGRESS',
            }),
          });
        }
      } catch (e: any) {
        void message.error(e.message);
      }
    },
    show: ({ currentUser, existingRecord }) => {
      const hasCbamDeclarantIdMissingMilestone =
        existingRecord?.milestones?.some(
          (milestone: WorkOrderMilestone) =>
            milestone.name === WorkOrderMilestoneName.CBAM_DECLARANT_ID_MISSING,
        );
      return (
        isCbamDetectionEnabled &&
        currentUser.accountType === AccountType.OPERATOR &&
        isEuCustomsDeclaration(existingRecord as any) &&
        hasCbamDeclarantIdMissingMilestone
      );
    },
  };
